import React, { useEffect, useState } from "react";
import { Sparkles, ShieldCheck, AlertTriangle, CheckCircle2, XCircle, ThumbsUp, ThumbsDown, Flag, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

const TIER_LABEL = { A: "Tier A · Strongest", B: "Tier B · Strong", C: "Tier C · Supporting", D: "Tier D · Low strength" };

const ConfidencePill = ({ confidence }) => {
  const map = {
    high: { label: "High confidence", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
    medium: { label: "Medium confidence", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
    low: { label: "Limited evidence", cls: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" },
    insufficient: { label: "Insufficient evidence", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  }[confidence] || { label: confidence || "medium confidence", cls: "bg-muted" };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${map.cls}`}>
      <ShieldCheck className="h-2.5 w-2.5" /> {map.label}
    </span>
  );
};

/** Public consumer-facing summary of the sustainability assessment. */
const AiSummary = ({ product, methodology }) => {
  const [feedback, setFeedback] = useState(null);
  const [voting, setVoting] = useState(false);
  const [openDisagree, setOpenDisagree] = useState(false);
  const [dimension, setDimension] = useState("material");
  const [note, setNote] = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");

  const explanation = methodology?.explanation?.trim();
  const overallNotes = methodology?.overall_notes?.trim();
  const score = product?.sustainability_score ?? 0;
  const confidence = methodology?.confidence || "medium";
  const strengths = methodology?.strengths || [];
  const limitations = methodology?.limitations || [];

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${product.id}/feedback-summary`);
        setFeedback(data);
      } catch { /* silent */ }
    })();
  }, [product.id]);

  const submitVote = async (vote, extra = {}) => {
    setVoting(true);
    try {
      await api.post(`/products/${product.id}/feedback`, { vote, ...extra });
      trackEvent("EVIDENCE_VIEW", { product_id: product.id, meta: { vote } });
      const { data } = await api.get(`/products/${product.id}/feedback-summary`);
      setFeedback(data);
      toast.success("Thanks — feedback recorded");
    } catch { toast.error("Couldn't record feedback"); }
    finally { setVoting(false); }
  };

  const submitDisagreement = async (e) => {
    e.preventDefault();
    await submitVote("disagree", { dimension, note: note.trim() || undefined,
                                    evidence_url: evidenceUrl.trim() || undefined });
    setOpenDisagree(false); setNote(""); setEvidenceUrl("");
  };

  const fallback = `${product?.brand ?? "This product"} scored ${score}/100 based on ${methodology?.evidence_criteria_count ?? "our"} evidence-backed criteria.`;

  return (
    <div className="card-premium p-6 sm:p-8" data-testid="ai-summary-card">
      <div className="flex items-start gap-3">
        <div className="inline-grid place-items-center h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 shrink-0">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg sm:text-xl font-bold tracking-tight">Why this score</h3>
            <ConfidencePill confidence={confidence} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            EcoWallet Sustainability Score is an evidence-based comparative assessment based on publicly available information. Scores may change as new evidence becomes available.
          </p>
        </div>
      </div>

      <p className="mt-5 text-base sm:text-lg leading-relaxed text-foreground" data-testid="ai-summary-text">
        {explanation || fallback}
      </p>

      {overallNotes && (
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-4" data-testid="ai-summary-notes">
          {overallNotes}
        </p>
      )}

      {/* Strengths & limitations */}
      {(strengths.length > 0 || limitations.length > 0) && (
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          {strengths.length > 0 && (
            <div className="rounded-2xl border border-emerald-200/60 dark:border-emerald-900/50 bg-emerald-50/60 dark:bg-emerald-950/30 p-4" data-testid="ai-summary-strengths">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 className="h-3 w-3" /> Strengths
              </div>
              <ul className="mt-2 space-y-2 text-sm">
                {strengths.map((s, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-1">✓</span>
                    <div className="min-w-0">
                      <div className="font-semibold">{s.label}</div>
                      <div className="text-xs text-muted-foreground truncate">{s.evidence}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {limitations.length > 0 && (
            <div className="rounded-2xl border border-amber-200/60 dark:border-amber-900/50 bg-amber-50/60 dark:bg-amber-950/30 p-4" data-testid="ai-summary-limitations">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-amber-700 dark:text-amber-300">
                <XCircle className="h-3 w-3" /> Limitations
              </div>
              <ul className="mt-2 space-y-2 text-sm">
                {limitations.map((l, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-amber-600 mt-1">!</span>
                    <div className="min-w-0">
                      <div className="font-semibold">{l.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {l.state === "insufficient" ? "Not enough public evidence to verify this criterion." : l.reason}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Evidence footer */}
      {methodology?.evidence_criteria_count != null && (
        <div className="mt-5 text-[11px] text-muted-foreground flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3" />
          Based on {methodology.evidence_criteria_count}/{methodology.total_criteria_count} criteria with public evidence
          {methodology.tier_a_or_b_count != null && ` · ${methodology.tier_a_or_b_count} Tier A/B source(s)`}.
        </div>
      )}

      {/* Feedback bar */}
      <div className="mt-6 pt-5 border-t border-border" data-testid="feedback-bar">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="text-xs text-muted-foreground">
            {feedback?.total > 0 ? (
              <span data-testid="feedback-summary">
                <span className="font-bold text-foreground">{feedback.agree_pct}%</span> of {feedback.total} shopper{feedback.total !== 1 && "s"} agree with this score
              </span>
            ) : "Was this assessment helpful?"}
          </div>
          <div className="flex items-center gap-1">
            <button disabled={voting} onClick={() => submitVote("agree")} className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-emerald-100 dark:hover:bg-emerald-900/40 flex items-center gap-1 disabled:opacity-50" data-testid="fb-agree">
              <ThumbsUp className="h-3 w-3" /> I agree
            </button>
            <button disabled={voting} onClick={() => setOpenDisagree(true)} className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-amber-100 dark:hover:bg-amber-900/40 flex items-center gap-1 disabled:opacity-50" data-testid="fb-disagree">
              <ThumbsDown className="h-3 w-3" /> I disagree
            </button>
            <button disabled={voting} onClick={() => submitVote("incorrect")} className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-red-100 dark:hover:bg-red-900/40 flex items-center gap-1 disabled:opacity-50" data-testid="fb-incorrect">
              <Flag className="h-3 w-3" /> Incorrect
            </button>
          </div>
        </div>
        <p className="mt-2 text-[10px] text-muted-foreground italic">
          Your feedback helps EcoWallet improve future research. Votes never directly change the score — evidence does.
        </p>
      </div>

      {/* Disagreement dialog */}
      {openDisagree && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setOpenDisagree(false)} data-testid="disagree-modal">
          <div className="card-premium max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-lg font-bold">What did we get wrong?</h4>
            <p className="text-xs text-muted-foreground mt-1">Your input goes to our review queue. It doesn't automatically change the score.</p>
            <form onSubmit={submitDisagreement} className="mt-4 space-y-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Which area?</label>
                <select value={dimension} onChange={(e) => setDimension(e.target.value)} className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-2.5 text-sm" data-testid="disagree-dim">
                  <option value="material">Material / ingredients</option>
                  <option value="certification">Certification</option>
                  <option value="packaging">Packaging</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="durability">Durability</option>
                  <option value="end_of_life">End-of-life</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">What's inaccurate?</label>
                <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-2.5 text-sm" data-testid="disagree-note" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Plus className="h-3 w-3" /> Supporting URL (optional)</label>
                <input type="url" value={evidenceUrl} onChange={(e) => setEvidenceUrl(e.target.value)} placeholder="https://..." className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-2.5 text-sm" data-testid="disagree-url" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpenDisagree(false)} className="text-xs px-4 py-2 rounded-full bg-muted">Cancel</button>
                <button type="submit" disabled={voting} className="text-xs px-4 py-2 rounded-full bg-primary text-primary-foreground disabled:opacity-50" data-testid="disagree-submit">Submit feedback</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiSummary;
export { TIER_LABEL, ConfidencePill };
